import React, {useContext, useEffect, useState} from "react";
import {AccountContext} from "../context/AccountProvider";
import Cookies from "universal-cookie";
import {t} from "i18next";
import {useNavigate} from "react-router-dom";
import {generateOtp, matchOtp} from "../service/userOtpApi";
import {toast} from "react-toastify";
import {ChevronDownIcon} from "@radix-ui/react-icons";
import {Transition} from "@headlessui/react";
import {countryCodes} from "../data/CountryCode";
import {useAxiosPrivate} from "../hooks/useAxiosPrivate";
import {axiosPublic} from "../service/axios";

export const Login = () => {
    const navigate = useNavigate();
    const axiosPrivate = useAxiosPrivate();

    const {setAccount, setAccessToken} = useContext(AccountContext);

    const [showContinueWithEmailSection, setShowContinueWithEmailSection] = useState(false);

    const [emailId, setEmailId] = useState("");

    const [isSendOtpButtonDisabled, setIsSendOtpButtonDisabled] = useState(true);
    const [isSendOtpSuccess, setIsSendOtpSuccess] = useState(false);

    const [showContinueWithPhoneSection, setShowContinueWithPhoneSection] = useState(true);

    const [phoneNumber, setPhoneNumber] = useState({
        charCode: 'IN',
        dialingCode: '+91',
        countryName: 'India',
        number: ''
    });

    const [isCountryCodeDropdownOpen, setIsCountryCodeDropdownOpen] = useState(false);
    const [isSelectingCountryCode, setIsSelectingCountryCode] = useState(false);

    const [errorMessage, setErrorMessage] = useState("");

    const [otpData, setOtpData] = useState({});
    const [otp, setOtp] = useState("");

    const [isVerifyOtpButtonDisabled, setIsVerifyOtpButtonDisabled] = useState(true);

    const validateEmail = (email) => {
        return /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(email);
    }

    const validateEmailId = (e) => {
        if (e.target.value !== "") {
            if (!validateEmail(e.target.value)) {
                setErrorMessage(t("LOGIN_PAGE.EMAIL_INPUT.ERROR.INVALID_INPUT"));
            } else {
                setIsSendOtpButtonDisabled(false);
                setErrorMessage("");
            }
        } else {
            setIsSendOtpButtonDisabled(true);
            setErrorMessage("");
        }
    }

    useEffect(() => {
        if (validateEmail(emailId)) {
            setIsSendOtpButtonDisabled(false);
        } else {
            setIsSendOtpButtonDisabled(true);
        }
    }, [emailId]);

    const handleCountryCodeSelect = (countryDetails) => {
        setIsSelectingCountryCode(false);
        setIsCountryCodeDropdownOpen(false);
        setPhoneNumber({
            ...phoneNumber,
            charCode: countryDetails.countryCode,
            dialingCode: countryDetails.countryDialingCode,
            countryName: countryDetails.countryName,
        });
    }

    const isNumberValid = (phone) => {
        let reg = /^\d+$/;
        return reg.test(phone);
    }

    const validatePhoneNumber = (e) => {
        console.log("Validate Phone Number: ", e.target.value);
        if (e.target.value !== "") {
            if (!isNumberValid(e.target.value) || e.target.value.length < 10) {
                setErrorMessage(t("LOGIN_PAGE.PHONE_INPUT.ERROR.INVALID_INPUT"));
            } else {
                setIsSendOtpButtonDisabled(false);
                setErrorMessage("");
            }
        } else {
            setIsSendOtpButtonDisabled(true);
            setErrorMessage("");
        }
    }

    useEffect(() => {
        if (!isNumberValid(phoneNumber.number) || phoneNumber.number.length < 10) {
            setIsSendOtpButtonDisabled(true);
        } else {
            setIsSendOtpButtonDisabled(false);
        }
    }, [phoneNumber]);

    useEffect(() => {
        if (otp.length === 6 && otp) {
            setIsVerifyOtpButtonDisabled(false);
        } else {
            setIsVerifyOtpButtonDisabled(true);
        }
    }, [otp]);

    const sendOtpHandler = () => {
        setIsSendOtpButtonDisabled(true);
        let verificationType = showContinueWithEmailSection ? "email" : "phone";
        let verifier = showContinueWithEmailSection ? emailId.replaceAll(" ", "") : {
            charCode: phoneNumber.charCode,
            dialingCode: phoneNumber.dialingCode,
            countryName: phoneNumber.countryName,
            number: phoneNumber.number.replaceAll(" ", "")
        };

        console.log("Verification Type: ", verificationType);
        console.log("Verifier: ", verifier);

        generateOtp(axiosPublic, verifier, verificationType, "login")
            .then((result) => {
                console.log("(Login.jsx) Send OTP Result: ", result);

                if (result.success) {
                    setOtpData(result.data);
                    setIsSendOtpSuccess(true);
                    toast.success(result.message, {
                        autoClose: 60000,
                        closeOnClick: false
                    });
                }
            })
            .catch((error) => {
                console.log("(Login.jsx) Error while sending OTP: ", error);
                setIsSendOtpSuccess(false);
                toast.error(error.data.message);
            })
            .finally(() => {
                setIsSendOtpButtonDisabled(false);
            });
    }

    const matchOtpHandler = () => {
        if (otpData && otpData.userId && otp) {
            toast.dismiss();

            matchOtp(axiosPrivate, otpData.userId, otp, "login")
                .then((result) => {
                    console.log("Match OTP Result in Login Component: ", result);

                    if (result.success) {
                        if (result.data) {
                            setAccount((prevState) => ({
                                ...prevState,
                                ...result.data.user,
                            }));
                            setAccessToken(result.data.accessToken);
                            toast.success(result.message);
                        } else {
                            toast.info(result.message);
                        }
                    } else {
                        toast.error(result.message);
                    }
                })
                .catch((error) => {
                    console.log("(Login.jsx) Error while matching OTP: ", error);
                    toast.error(error.data.message);
                });
        }
    }

    useEffect(() => {
        toast.dismiss();
        const result = new Cookies().get("authRequired")
        if (result) {
            toast.warn(t("CONSTANTS.AUTHENTICATION_REQUIRED"));
        }

        new Cookies().remove("authRequired");
    }, []);

    return (
        <div className="h-[100dvh] w-full flex portrait:flex-col landscape:flex-row items-center justify-center">
            {/* Background */}
            <div className="absolute inset-0 h-dvh">
                <img
                    src="/assets/home-page-hero-section-bg.png"
                    alt=""
                    className="absolute w-full h-full object-bottom object-cover shadow-lg"
                />
                <div
                    className="absolute h-full w-full flex
                        items-end portrait:justify-center landscape:px-[4rem]"
                >
                    <img
                        src="/assets/hero-section-home-page-banner-statue.png"
                        alt=""
                        className="portrait:h-[20%] portrait:xs:h-[22.5%]
                            portrait:md:h-[27.5%] portrait:jk:h-[37.5%] portrait:lg:h-[50%]
                            landscape:h-[50%] landscape:jk:h-[60%] landscape:lg:h-[70%]
                            aspect-auto object-contain"
                    />
                    <img
                        src="/assets/hero-section-home-page-title.svg"
                        alt=""
                        className="portrait:h-[13%] portrait:xs:h-[15%]
                            portrait:md:h-[20%] portrait:jk:h-[27.5%] portrait:lg:h-[37.5%]
                            landscape:h-[35%] landscape:jk:h-[42.5%] landscape:lg:h-[50%]
                            aspect-auto object-contain
                            portrait:mb-[2rem] landscape:mb-[2rem]"
                    />
                </div>
            </div>

            {/* Login Card */}
            <div
                className="z-10 h-full w-full flex flex-1 flex-col items-center portrait:justify-center
                    landscape:justify-start landscape:items-end
                    portrait:p-[2rem] portrait:jk:p-[3.5rem] portrait:lg:p-[5rem]
                    landscape:p-[1.5rem] landscape:jk:p-[1.75rem] landscape:lg:p-[2rem]"
            >
                <div
                    className="flex flex-col bg-white
                        portrait:w-full portrait:sm:w-[25rem] portrait:jk:w-[27.5rem] portrait:lg:w-[40rem]

                        portrait:gap-y-[0.75rem] portrait:jk:gap-y-[1rem] portrait:lg:gap-y-[1.5rem]

                        portrait:px-[1.25rem] portrait:py-[0.95rem]
                        portrait:jk:px-[1.5rem] portrait:jk:py-[1.1rem]
                        portrait:lg:px-[2rem] portrait:lg:py-[1.5rem]

                        portrait:rounded-xl landscape:rounded-xl

                        landscape:gap-y-[0.4rem] landscape:jk:gap-y-[0.55rem] landscape:lg:gap-y-[1rem]

                        landscape:w-[18.75rem] landscape:md:w-[20rem]
                        landscape:jk:w-[23rem] landscape:lg:w-[30rem]

                        landscape:px-[1.25rem] landscape:py-[0.95rem]
                        landscape:jk:px-[1.5rem] landscape:jk:py-[1.1rem]
                        landscape:lg:px-[1.75rem] landscape:lg:py-[1.25rem]

                        shadow-[0_13px_27px_-5px_#32325d40,_0_8px_16px_-8px_#0000004d]"
                >
                    {/* Header */}
                    <div className={"flex flex-col gap-y-[0.35rem]"}>
                        <h1
                            className="leading-tight
                                portrait:text-[1.5rem] portrait:sm:text-[1.65rem]
                                portrait:jk:text-[2rem] portrait:lg:text-[2.75rem]
                                landscape:text-[1.25rem] landscape:jk:text-[1.45rem] landscape:lg:text-[1.75rem]
                                font-custom-bold text-gray-900"
                        >
                            {t("LOGIN_PAGE.GREETING.MAIN")}
                        </h1>

                        <p
                            className="leading-tight
                                portrait:text-[1.15rem] portrait:sm:text-[1.25rem]
                                portrait:jk:text-[1.5rem] portrait:lg:text-[2rem]
                                landscape:text-[0.85rem] landscape:jk:text-[1rem] landscape:lg:text-[1.225rem]
                                font-custom-semi-bold text-gray-500"
                        >
                            {t("LOGIN_PAGE.GREETING.SUB")}
                        </p>
                    </div>

                    {/* Form - Body */}
                    <div
                        className="flex flex-col items-center
                            portrait:gap-[1rem] portrait:jk:gap-[1.25rem] portrait:lg:gap-[1.75rem]
                            landscape:gap-[1rem] landscape:jk:gap-[1.25rem] landscape:lg:gap-[1.5rem]"
                    >
                        {/* Email ID Section */}
                        {
                            showContinueWithEmailSection && (
                                <div className="custom-input-container">
                                    <label htmlFor="email">
                                        {t("LOGIN_PAGE.EMAIL_INPUT.LABEL")}
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={emailId}
                                        onChange={(e) => {
                                            setEmailId(e.target.value);
                                            setErrorMessage("");
                                        }}
                                        id="email"
                                        className={`custom-input w-full`}
                                        onBlur={validateEmailId}
                                    />

                                    {errorMessage && (
                                        <p
                                            id="email-error"
                                            className="custom-input-error-message"
                                        >
                                            {errorMessage}
                                        </p>
                                    )}
                                </div>
                            )
                        }

                        {/* Phone Number Section */}
                        {
                            showContinueWithPhoneSection && (
                                <div className="custom-phone-number-input">
                                    <label htmlFor="phoneNumber">
                                        {t("LOGIN_PAGE.PHONE_INPUT.LABEL")}
                                    </label>
                                    <div
                                        className="relative flex-1 flex flex-col
                                            portrait:gap-2 portrait:jk:gap-4 landscape:gap-2"
                                    >
                                        {/* Country Code | Phone Number Input */}
                                        <div
                                            className="custom-phone-number-input-container"
                                        >
                                            <button
                                                onBlur={() => {
                                                    if (!isSelectingCountryCode) {
                                                        setIsCountryCodeDropdownOpen(false);
                                                    }
                                                }}
                                                onClick={() => setIsCountryCodeDropdownOpen(!isCountryCodeDropdownOpen)}
                                            >
                                                <img
                                                    src={`assets/country_flags/${phoneNumber.charCode}.png`}
                                                    className={"w-[1vw] aspect-auto"}
                                                    alt={phoneNumber.countryName}
                                                />
                                                <p
                                                    className={""}
                                                    aria-selected={"false"}
                                                >
                                                    {phoneNumber.dialingCode}
                                                </p>
                                                <ChevronDownIcon
                                                    // onClick={() => setIsCountryCodeDropdownOpen(!isCountryCodeDropdownOpen)}
                                                    className={`${isCountryCodeDropdownOpen ? 'rotate-180' : 'rotate-0'} `}
                                                    aria-hidden="true"
                                                    stroke="#292D32"
                                                    strokeWidth={"0.1vh"}
                                                />
                                            </button>
                                            <input
                                                type="phone"
                                                name="phoneNumber"
                                                className=""
                                                placeholder="7734567890"
                                                value={phoneNumber.number || ""}
                                                onChange={(e) => {
                                                    setPhoneNumber({
                                                        ...phoneNumber,
                                                        number: e.target.value
                                                    })

                                                    setErrorMessage("");
                                                }}
                                                onBlur={validatePhoneNumber}
                                                required
                                            />
                                        </div>

                                        {/* Country Code Dropdown */}
                                        <div className={``}>
                                            <Transition show={isCountryCodeDropdownOpen}>
                                                <div
                                                    className={`bg-white absolute left-0 right-0 z-[100]
                                                        origin-top-right rounded-md shadow-2xl mt-[0.5vh]
                                                        
                                                        landscape:mx-[0.75rem] landscape:jk:mx-[0.9rem] landscape:lg:mx-[1.15rem]
                                                        
                                                        border border-gray-700
                                                        data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 
                                                        data-[enter]:duration-100 data-[enter]:ease-out 
                                                        data-[leave]:duration-75 data-[leave]:ease-in`}
                                                >
                                                    <div
                                                        className={"w-full flex-1 overflow-hidden relative rounded-md"}>
                                                        <div
                                                            className="flex-1 flex flex-col max-h-[30vh] w-full overflow-y-scroll"
                                                        >
                                                            {
                                                                countryCodes.map((country, index) => (
                                                                    <button
                                                                        key={index}
                                                                        className={`custom-input-dropdown-list-button`}
                                                                        onMouseDown={() => setIsSelectingCountryCode(true)}
                                                                        onClick={() => handleCountryCodeSelect(country)}
                                                                    >
                                                                        <img
                                                                            src={country.countryFlagSrc}
                                                                            alt={country.countryName}
                                                                        />
                                                                        <p
                                                                            className={`text-nowrap`}
                                                                        >
                                                                            {`${country.countryName}  (${country.countryDialingCode})`}
                                                                        </p>
                                                                    </button>
                                                                ))
                                                            }
                                                        </div>
                                                    </div>
                                                </div>
                                            </Transition>
                                        </div>
                                    </div>

                                    {errorMessage && (
                                        <p
                                            id="phone-error"
                                            className="custom-input-error-message"
                                        >
                                            {errorMessage}
                                        </p>
                                    )}
                                </div>
                            )
                        }

                        {/* Send OTP */}
                        <button
                            disabled={isSendOtpButtonDisabled}
                            onClick={sendOtpHandler}
                            className={`${!isSendOtpButtonDisabled && 'arrow-button'} 
                                w-full inline-flex items-center justify-center bg-[#D26A53] gap-x-[0.35rem]
                                landscape:rounded-md portrait:rounded-md portrait:jk:rounded-lg
                                
                                portrait:py-[0.5rem] portrait:sm:py-[0.45rem] portrait:jk:py-[0.55rem] portrait:lg:py-[0.75rem]
                                landscape:py-[0.35rem] landscape:jk:py-[0.4rem] landscape:lg:py-[0.5rem]
                                
                                hover:ring-[#5F0F40] hover:ring-offset-1 hover:ring-4 
                                disabled:cursor-not-allowed disabled:bg-slate-400/50 
                                disabled:ring-0 disabled:ring-offset-0
                                transition-all duration-300 ease-in-out`}
                        >
                            <p
                                className="line-clamp-1 text-white leading-tight font-custom-medium
                                    portrait:text-[1rem] portrait:sm:text-[1rem]
                                    portrait:jk:text-[1.2rem] portrait:lg:text-[1.5rem]
                                    landscape:text-[0.8rem] landscape:jk:text-[0.9rem] landscape:lg:text-[1rem]"
                            >
                                {t("LOGIN_PAGE.OTP_BUTTON.SEND")}
                            </p>
                            <svg
                                viewBox="0 0 6 6"
                                fill="none"
                                id={"arrow-icon"}
                                className={`portrait:h-[0.85rem] portrait:w-[0.85rem]
                                    portrait:sm:h-[0.95rem] portrait:sm:w-[0.95rem]
                                    portrait:jk:h-[0.9rem] portrait:jk:w-[0.9rem]
                                    landscape:h-[0.65rem] landscape:w-[0.65rem]
                                    landscape:md:h-[0.75rem] landscape:md:w-[0.75rem]
                                    landscape:jk:h-[0.85rem] landscape:jk:w-[0.85rem]`}
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M3 1L5 3L3 5"
                                    id={"arrow-icon-tip"}
                                    stroke="white"
                                    strokeLinecap="square"
                                />
                                <path
                                    d="M1 3L4 3"
                                    id={"arrow-icon-line"}
                                    stroke="white"
                                    strokeLinecap="square"
                                />
                            </svg>
                        </button>

                        {/* Verify OTP */}
                        {isSendOtpSuccess && (
                            <>
                                {/* Input */}
                                <div className="custom-input-container">
                                    <label htmlFor="otp">
                                        {t("LOGIN_PAGE.OTP_BUTTON.ENTER")}
                                    </label>
                                    <input
                                        type={"number"}
                                        name="otp"
                                        value={otp}
                                        onChange={(e) => setOtp(e.target.value)}
                                        id="otp"
                                        className={`custom-input`}
                                    />
                                </div>

                                {/* Button*/}
                                <button
                                    disabled={isVerifyOtpButtonDisabled}
                                    onClick={matchOtpHandler}
                                    className={`${!isVerifyOtpButtonDisabled && 'arrow-button'} 
                                        w-full inline-flex items-center justify-center bg-[#D26A53] gap-x-[0.35rem]
                                        landscape:rounded-md portrait:rounded-md portrait:jk:rounded-lg
                                        
                                        portrait:py-[0.5rem] portrait:sm:py-[0.45rem] portrait:jk:py-[0.55rem] portrait:lg:py-[0.75rem]
                                        landscape:py-[0.35rem] landscape:jk:py-[0.4rem] landscape:lg:py-[0.5rem]
                                        
                                        hover:ring-[#5F0F40] hover:ring-offset-1 hover:ring-4 
                                        disabled:cursor-not-allowed disabled:bg-slate-400/50 
                                        disabled:ring-0 disabled:ring-offset-0
                                        transition-all duration-300 ease-in-out`}
                                >
                                    <p
                                        className="line-clamp-1 text-white leading-tight font-custom-medium
                                            portrait:text-[1rem] portrait:sm:text-[1rem]
                                            portrait:jk:text-[1.2rem] portrait:lg:text-[1.5rem]
                                            landscape:text-[0.8rem] landscape:jk:text-[0.9rem] landscape:lg:text-[1rem]"
                                    >
                                        {t("LOGIN_PAGE.OTP_BUTTON.VERIFY")}
                                    </p>
                                    <svg
                                        viewBox="0 0 6 6"
                                        fill="none"
                                        id={"arrow-icon"}
                                        className={`portrait:h-[0.85rem] portrait:w-[0.85rem]
                                            portrait:sm:h-[0.95rem] portrait:sm:w-[0.95rem]
                                            portrait:jk:h-[0.9rem] portrait:jk:w-[0.9rem]
                                            landscape:h-[0.65rem] landscape:w-[0.65rem]
                                            landscape:md:h-[0.75rem] landscape:md:w-[0.75rem]
                                            landscape:jk:h-[0.85rem] landscape:jk:w-[0.85rem]`}
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M3 1L5 3L3 5"
                                            id={"arrow-icon-tip"}
                                            stroke="white"
                                            strokeLinecap="square"
                                        />
                                        <path
                                            d="M1 3L4 3"
                                            id={"arrow-icon-line"}
                                            stroke="white"
                                            strokeLinecap="square"
                                        />
                                    </svg>
                                </button>
                            </>
                        )}

                        {/* Divider */}
                        <div className={"w-full relative flex items-center justify-center"}>
                            {/* Line */}
                            <div className={"absolute w-full h-[0.1rem] bg-[#d8dee4] rounded-full"}/>

                            {/* Text */}
                            <p
                                className="z-10 bg-white px-[0.5rem] font-custom-medium
                                    portrait:text-[0.85rem] portrait:md:text-[0.8rem]
                                    portrait:jk:text-[1.2rem] portrait:lg:text-[1.45rem]
                                    landscape:text-[0.5rem] landscape:jk:text-[0.65rem] landscape:lg:text-[0.8rem]"
                            >
                                {t("LOGIN_PAGE.DIVIDER_TEXT")}
                            </p>
                        </div>

                        {/* Alternate Authentication Options */}
                        <div
                            className="w-full flex flex-col
                                portrait:gap-[1rem] portrait:jk:gap-[1.25rem] portrait:lg:gap-[1.75rem]
                                landscape:gap-[0.5rem] landscape:jk:gap-[0.75rem] landscape:lg:gap-[1rem]"
                        >
                            {/* Continue With Phone */}
                            {
                                showContinueWithEmailSection && (
                                    <div className={"inline-flex items-center justify-between w-full"}>
                                        <button
                                            onClick={() => {
                                                setShowContinueWithPhoneSection(true);
                                                setShowContinueWithEmailSection(false);
                                                setEmailId("");
                                                setErrorMessage("");
                                                setIsSendOtpButtonDisabled(true);
                                            }}
                                            type="button"
                                            role="link"
                                            tabIndex={0}
                                            aria-label="Register"
                                            className={`arrow-button flex flex-1 items-center justify-center 
                                                gap-x-[0.5rem] bg-[#610329] hover:bg-[#000022] text-white
                                                
                                                landscape:rounded-md portrait:rounded-md portrait:jk:rounded-lg
                                                
                                                portrait:py-[0.65rem] portrait:md:py-[0.5rem] 
                                                portrait:jk:py-[0.6rem] portrait:lg:py-[0.75rem]
                                                landscape:py-[0.45rem] landscape:jk:py-[0.5rem] 
                                                landscape:lg:py-[0.6rem]
                                                
                                                hover:ring-[#5F0F40] hover:ring-offset-1 hover:ring-4
                                                transition-all duration-300 ease-in-out`}
                                        >
                                            <p
                                                className="line-clamp-1 text-white leading-tight font-custom-medium
                                                    portrait:text-[1rem] portrait:md:text-[1rem]
                                                    portrait:jk:text-[1.2rem] portrait:lg:text-[1.5rem]
                                                    landscape:text-[0.75rem] landscape:md:text-[0.8rem]
                                                    landscape:jk:text-[0.9rem] landscape:lg:text-[1rem]"
                                            >
                                                {t("LOGIN_PAGE.ALTERNATE_AUTH_OPTIONS.CONTINUE_WITH_PHONE")}
                                            </p>
                                            <svg
                                                viewBox="0 0 6 6"
                                                fill="none"
                                                id={"arrow-icon"}
                                                className={`portrait:h-[0.85rem] portrait:w-[0.85rem]
                                                    portrait:sm:h-[0.95rem] portrait:sm:w-[0.95rem]
                                                    portrait:jk:h-[0.9rem] portrait:jk:w-[0.9rem]
                                                    landscape:h-[0.65rem] landscape:w-[0.65rem]
                                                    landscape:md:h-[0.75rem] landscape:md:w-[0.75rem]
                                                    landscape:jk:h-[0.85rem] landscape:jk:w-[0.85rem]`}
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M3 1L5 3L3 5"
                                                    id={"arrow-icon-tip"}
                                                    stroke="white"
                                                    strokeLinecap="square"
                                                />
                                                <path
                                                    d="M1 3L4 3"
                                                    id={"arrow-icon-line"}
                                                    stroke="white"
                                                    strokeLinecap="square"
                                                />
                                            </svg>
                                        </button>
                                    </div>
                                )
                            }

                            {/* Continue With Email */}
                            {
                                showContinueWithPhoneSection && (
                                    <div className={"inline-flex items-center justify-between w-full"}>
                                        <button
                                            onClick={() => {
                                                setShowContinueWithEmailSection(true);
                                                setShowContinueWithPhoneSection(false);
                                                setPhoneNumber({
                                                    charCode: 'IN',
                                                    dialingCode: '+91',
                                                    countryName: 'India',
                                                    number: ''
                                                });
                                                setErrorMessage("");
                                                setIsSendOtpButtonDisabled(true);
                                            }}
                                            type="button"
                                            role="link"
                                            tabIndex={0}
                                            aria-label="Register"
                                            className={`arrow-button flex flex-1 items-center justify-center 
                                                gap-x-[0.5rem] bg-[#610329] hover:bg-[#000022] text-white
                                                
                                                landscape:rounded-md portrait:rounded-md portrait:jk:rounded-lg
                                                
                                                portrait:py-[0.65rem] portrait:md:py-[0.5rem] 
                                                portrait:jk:py-[0.6rem] portrait:lg:py-[0.75rem]
                                                landscape:py-[0.45rem] landscape:jk:py-[0.5rem] landscape:lg:py-[0.6rem]
                                                
                                                hover:ring-[#5F0F40] hover:ring-offset-1 hover:ring-4
                                                transition-all duration-300 ease-in-out`}
                                        >
                                            <p
                                                className="line-clamp-1 text-white leading-tight font-custom-medium
                                                    portrait:text-[1rem] portrait:md:text-[1rem]
                                                    portrait:jk:text-[1.2rem] portrait:lg:text-[1.5rem]
                                                    landscape:text-[0.75rem] landscape:md:text-[0.8rem]
                                                    landscape:jk:text-[0.9rem] landscape:lg:text-[1rem]"
                                            >
                                                {t("LOGIN_PAGE.ALTERNATE_AUTH_OPTIONS.CONTINUE_WITH_EMAIL")}
                                            </p>
                                            <svg
                                                viewBox="0 0 6 6"
                                                fill="none"
                                                id={"arrow-icon"}
                                                className={`portrait:h-[0.85rem] portrait:w-[0.85rem]
                                                    portrait:sm:h-[0.95rem] portrait:sm:w-[0.95rem]
                                                    portrait:jk:h-[0.9rem] portrait:jk:w-[0.9rem]
                                                    landscape:h-[0.65rem] landscape:w-[0.65rem]
                                                    landscape:md:h-[0.75rem] landscape:md:w-[0.75rem]
                                                    landscape:jk:h-[0.85rem] landscape:jk:w-[0.85rem]`}
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M3 1L5 3L3 5"
                                                    id={"arrow-icon-tip"}
                                                    stroke="white"
                                                    strokeLinecap="square"
                                                />
                                                <path
                                                    d="M1 3L4 3"
                                                    id={"arrow-icon-line"}
                                                    stroke="white"
                                                    strokeLinecap="square"
                                                />
                                            </svg>
                                        </button>
                                    </div>
                                )
                            }

                            {/* Register */}
                            <div className={"inline-flex items-center justify-between w-full"}>
                                <button
                                    onClick={() => navigate('/register')}
                                    type="button"
                                    role="link"
                                    tabIndex={0}
                                    aria-label="Register"
                                    className={`arrow-button flex flex-1 items-center justify-center 
                                        gap-x-[0.5rem] bg-[#610329] hover:bg-[#000022] text-white
                                        
                                        landscape:rounded-md portrait:rounded-md portrait:jk:rounded-lg
                                        portrait:py-[0.65rem] portrait:md:py-[0.5rem] 
                                        portrait:jk:py-[0.6rem] portrait:lg:py-[0.75rem]
                                        landscape:py-[0.45rem] landscape:jk:py-[0.5rem] landscape:lg:py-[0.6rem]
                                        
                                        hover:ring-[#5F0F40] hover:ring-offset-1 hover:ring-4
                                        transition-all duration-300 ease-in-out`}
                                >
                                    <p
                                        className="line-clamp-1 text-white leading-tight font-custom-medium
                                            portrait:text-[1rem] portrait:md:text-[1rem]
                                            portrait:jk:text-[1.2rem] portrait:lg:text-[1.5rem]
                                            landscape:text-[0.75rem] landscape:md:text-[0.8rem]
                                            landscape:jk:text-[0.9rem] landscape:lg:text-[1rem]"
                                    >
                                        {t("LOGIN_PAGE.ALTERNATE_AUTH_OPTIONS.REGISTER")}
                                    </p>
                                    <svg
                                        viewBox="0 0 6 6"
                                        fill="none"
                                        id={"arrow-icon"}
                                        className={`portrait:h-[0.85rem] portrait:w-[0.85rem]
                                            portrait:sm:h-[0.95rem] portrait:sm:w-[0.95rem]
                                            portrait:jk:h-[0.9rem] portrait:jk:w-[0.9rem]
                                            landscape:h-[0.65rem] landscape:w-[0.65rem]
                                            landscape:md:h-[0.75rem] landscape:md:w-[0.75rem]
                                            landscape:jk:h-[0.85rem] landscape:jk:w-[0.85rem]`}
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M3 1L5 3L3 5"
                                            id={"arrow-icon-tip"}
                                            stroke="white"
                                            strokeLinecap="square"
                                        />
                                        <path
                                            d="M1 3L4 3"
                                            id={"arrow-icon-line"}
                                            stroke="white"
                                            strokeLinecap="square"
                                        />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
